/**
 * Systems Service - Frontend API för system och moduler
 */

import api from '@/services/api/client';
import type {
  System,
  SystemWithModules,
  Module,
  UserMenuResponse,
  CustomerSystem,
  CustomerModule,
} from '@/types/systems';

// =====================================================
// SYSTEMS
// =====================================================

export const getAllSystems = async (): Promise<System[]> => {
  const response = await api.get<{ success: boolean; data: System[] }>('/api/systems');
  return response.data.data;
};

export const getSystemsWithModules = async (): Promise<SystemWithModules[]> => {
  const response = await api.get<{ success: boolean; data: SystemWithModules[] }>('/api/systems/with-modules');
  return response.data.data;
};

export const getSystemById = async (id: string): Promise<System> => {
  const response = await api.get<{ success: boolean; data: System }>(`/api/systems/${id}`);
  return response.data.data;
};

export const createSystem = async (data: Partial<System>): Promise<System> => {
  const response = await api.post<{ success: boolean; data: System }>('/api/systems', data);
  return response.data.data;
};

export const updateSystem = async (id: string, data: Partial<System>): Promise<System> => {
  const response = await api.put<{ success: boolean; data: System }>(`/api/systems/${id}`, data);
  return response.data.data;
};

export const deleteSystem = async (id: string): Promise<void> => {
  await api.delete(`/api/systems/${id}`);
};

// =====================================================
// MODULES
// =====================================================

export const getAllModules = async (): Promise<Module[]> => {
  const response = await api.get<{ success: boolean; data: Module[] }>('/api/modules');
  return response.data.data;
};

export const getModulesBySystem = async (systemId: string): Promise<Module[]> => {
  const response = await api.get<{ success: boolean; data: Module[] }>(`/api/systems/${systemId}/modules`);
  return response.data.data;
};

export const getModuleById = async (id: string): Promise<Module> => {
  const response = await api.get<{ success: boolean; data: Module }>(`/api/modules/${id}`);
  return response.data.data;
};

export const createModule = async (data: Partial<Module>): Promise<Module> => {
  const response = await api.post<{ success: boolean; data: Module }>('/api/modules', data);
  return response.data.data;
};

export const updateModule = async (id: string, data: Partial<Module>): Promise<Module> => {
  const response = await api.put<{ success: boolean; data: Module }>(`/api/modules/${id}`, data);
  return response.data.data;
};

export const deleteModule = async (id: string): Promise<void> => {
  await api.delete(`/api/modules/${id}`);
};

// =====================================================
// USER MENU
// =====================================================

export const getUserMenu = async (): Promise<UserMenuResponse> => {
  const response = await api.get<{ success: boolean; data: UserMenuResponse }>('/api/menu');
  return response.data.data;
};

// =====================================================
// CUSTOMER CONFIGURATION
// =====================================================

export const getCustomerSystems = async (customerId: string): Promise<CustomerSystem[]> => {
  const response = await api.get<{ success: boolean; data: CustomerSystem[] }>(`/api/customers/${customerId}/systems`);
  return response.data.data;
};

export const enableSystemForCustomer = async (
  customerId: string,
  systemId: string,
  options?: { licenseKey?: string; licenseExpiresAt?: string; maxUsers?: number }
): Promise<void> => {
  await api.post(`/api/customers/${customerId}/systems/${systemId}/enable`, options);
};

export const disableSystemForCustomer = async (customerId: string, systemId: string): Promise<void> => {
  await api.post(`/api/customers/${customerId}/systems/${systemId}/disable`);
};

export const getCustomerModules = async (customerId: string): Promise<CustomerModule[]> => {
  const response = await api.get<{ success: boolean; data: CustomerModule[] }>(`/api/customers/${customerId}/modules`);
  return response.data.data;
};

export const enableModuleForCustomer = async (
  customerId: string,
  moduleId: string,
  settings?: Record<string, unknown>
): Promise<void> => {
  await api.post(`/api/customers/${customerId}/modules/${moduleId}/enable`, { settings });
};

export const disableModuleForCustomer = async (customerId: string, moduleId: string): Promise<void> => {
  await api.post(`/api/customers/${customerId}/modules/${moduleId}/disable`);
};

export default {
  // Systems
  getAllSystems,
  getSystemsWithModules,
  getSystemById,
  createSystem,
  updateSystem,
  deleteSystem,
  
  // Modules
  getAllModules,
  getModulesBySystem,
  getModuleById,
  createModule,
  updateModule,
  deleteModule,
  
  // User menu
  getUserMenu,
  
  // Customer config
  getCustomerSystems,
  enableSystemForCustomer,
  disableSystemForCustomer,
  getCustomerModules,
  enableModuleForCustomer,
  disableModuleForCustomer,
};

